import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { JSONContent } from '@tiptap/core';
import { EditorContent } from '@tiptap/react';
import {
  useTheme,
  MAP_TYPES,
  MAP_TYPE_LABELS,
  MAP_TYPE_DEFAULT_DESCRIPTIONS,
  getRegionDefinitions,
  getBlockDefaultInteractionMessage,
  isRichTextContentEmpty,
  sanitizeRichTextContent,
  type MapType,
  type MapRegionDefinition,
  type ThemeContextValue,
} from '@course-authoring/shared-types';
import {
  MapRenderer,
  safeGenerateRichTextHTML,
  safeParseHTMLToJSONContent,
  createRichTextRendererExtensions,
  type RichTextRendererExtensions,
} from '@course-authoring/ui-blocks';
import type { MapBlock, MapVersion } from '@course-authoring/shared-types';
import { ScopedI18nProvider } from '@course-authoring/i18n';
import { createRichTextEditorExtensions } from '../components/richTextEditorExtensions';
import { RichTextToolbar, type RichTextToolbarItem } from '../components/richTextToolbar';
import { createTokenRichTextToolbarStyles } from '../components/interactionEditorUtils';
import { useRichTextFieldEditor } from '../components/useRichTextFieldEditor';
import {
  mapKeys,
  useMapTranslations,
  useUpdateMapVersion,
  useDuplicateMapVersion,
  useDeleteMapVersion,
} from '../api/mapApi';
import {
  TranslationLanguageBanner,
  TranslationReplacementPrompt,
  TranslationStatusBadge,
  TranslationStatusNote,
} from '../components/TranslationStatusPrimitives';
import { useLanguageContext } from '../hooks/useLanguageContext';
import { getRememberedListPath } from '../hooks/useListPathMemory';
import {
  useMapTranslationPersistence,
  type MapEditorSnapshot,
  type RegionEditorState,
} from './mapEditor/useMapTranslationPersistence';

type ThemeTokens = ThemeContextValue['tokens'];

const EMPTY_DOC: JSONContent = { type: 'doc', content: [{ type: 'paragraph' }] };

const csvRichTextExtensions = createRichTextRendererExtensions();

// ---------------------------------------------------------------------------
// CSV helpers
// ---------------------------------------------------------------------------

function richTextToHTML(doc: JSONContent): string {
  return safeGenerateRichTextHTML(doc, csvRichTextExtensions as RichTextRendererExtensions);
}

function looksLikeHTML(text: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(text);
}

function htmlToDoc(html: string): JSONContent {
  return safeParseHTMLToJSONContent(html);
}

function escapeCsvField(value: string): string {
  if (value.includes('"') || value.includes(',') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function parseCsvRows(csv: string): Array<{ regionKey: string; name: string; description: string }> {
  const rows: Array<{ regionKey: string; name: string; description: string }> = [];
  const lines = csv.split('\n');
  let i = 0;

  // Skip header row
  if (lines.length > 0 && lines[0]!.toLowerCase().includes('region')) {
    i = 1;
  }

  while (i < lines.length) {
    const line = lines[i]!.trim();
    if (!line) { i++; continue; }

    const fields = parseCsvLine(line, lines, i);
    i = fields.nextIndex;

    if (fields.values.length >= 3) {
      rows.push({
        regionKey: fields.values[0]!.trim(),
        name: fields.values[1]!.trim(),
        description: fields.values[2]!.trim(),
      });
    }
  }

  return rows;
}

function parseCsvLine(
  line: string,
  allLines: string[],
  startIndex: number
): { values: string[]; nextIndex: number } {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;
  let lineIndex = startIndex;
  let fullLine = line;

  let j = 0;
  while (j < fullLine.length) {
    const char = fullLine[j]!;
    if (inQuotes) {
      if (char === '"') {
        if (j + 1 < fullLine.length && fullLine[j + 1] === '"') {
          current += '"';
          j += 2;
        } else {
          inQuotes = false;
          j++;
        }
      } else {
        current += char;
        j++;
      }
    } else if (char === '"') {
      inQuotes = true;
      j++;
    } else if (char === ',') {
      values.push(current);
      current = '';
      j++;
    } else {
      current += char;
      j++;
    }

    // Handle multi-line quoted fields
    if (inQuotes && j >= fullLine.length) {
      lineIndex++;
      if (lineIndex < allLines.length) {
        current += '\n';
        fullLine = fullLine + '\n' + allLines[lineIndex]!;
      }
    }
  }
  values.push(current);

  return { values, nextIndex: lineIndex + 1 };
}

function plainTextToDoc(text: string): JSONContent {
  if (!text) return EMPTY_DOC;
  const paragraphs = text.split('\n').map((line): JSONContent => {
    if (!line.trim()) return { type: 'paragraph' };
    return {
      type: 'paragraph',
      content: [{ type: 'text', text: line }],
    };
  });
  return { type: 'doc', content: paragraphs };
}

interface RegionRichFieldProps {
  region: RegionEditorState;
  onDescriptionChange: (regionKey: string, content: JSONContent) => void;
  tokens: ThemeTokens;
}

const MAP_REGION_TOOLBAR_ITEMS: RichTextToolbarItem[] = [
  { kind: 'action', action: 'bold' },
  { kind: 'action', action: 'italic' },
  { kind: 'action', action: 'underline' },
  { kind: 'separator' },
  { kind: 'action', action: 'bulletList', label: '*' },
  { kind: 'action', action: 'orderedList', label: '1.' },
  { kind: 'action', action: 'outdent' },
  { kind: 'action', action: 'indent' },
  { kind: 'separator' },
  { kind: 'action', action: 'link', label: '🔗' },
];

function RegionRichField({ region, onDescriptionChange, tokens }: RegionRichFieldProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const sanitizedContent = useMemo(() => sanitizeRichTextContent(region.description), [region.description]);
  const extensions = useMemo(
    () =>
      createRichTextEditorExtensions('Enter region description...', {
        enableLists: true,
        enableUnderline: true,
        enableLinks: true,
      }),
    []
  );
  const toolbarStyles = useMemo(() => createTokenRichTextToolbarStyles(tokens), [tokens]);

  const handleContentChange = useCallback(
    (content: JSONContent) => {
      onDescriptionChange(region.regionKey, content);
    },
    [region.regionKey, onDescriptionChange]
  );

  const editor = useRichTextFieldEditor({
    extensions,
    content: sanitizedContent,
    onContentChange: handleContentChange,
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    containerRef,
    editorClassName: 'map-region-editor',
    editorInlineStyle: `
      font-size: 14px;
      line-height: 1.5;
      color: ${tokens.text.primary};
      outline: none;
      min-height: 60px;
      padding: ${tokens.spacing.sm} ${tokens.spacing.md};
    `,
    autoFocus: false,
  });

  return (
    <div
      ref={containerRef}
      style={{
        border: `1px solid ${isFocused ? tokens.brand.primary : tokens.borders.default}`,
        borderRadius: tokens.radius.sm,
        backgroundColor: tokens.backgrounds.page,
        transition: 'border-color 0.15s',
      }}
    >
      {editor && isFocused && (
        <div style={{ padding: `${tokens.spacing.sm} ${tokens.spacing.md} 0` }}>
          <RichTextToolbar
            editor={editor}
            items={MAP_REGION_TOOLBAR_ITEMS}
            toolbarStyle={toolbarStyles.toolbar}
            buttonStyle={toolbarStyles.button}
            activeButtonStyle={toolbarStyles.activeButton}
            dividerStyle={toolbarStyles.divider}
          />
        </div>
      )}
      {editor && <EditorContent editor={editor} />}
    </div>
  );
}

export function MapEditorPage() {
  const { mapId } = useParams<{ mapId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);
  const {
    selectedLocale,
    selectedLanguageLabel,
    registerLocaleChangeGuard,
  } = useLanguageContext();
  const isTranslationMode = selectedLocale !== 'en';
  const translationQuery = useMapTranslations(mapId ?? '', selectedLocale, {
    enabled: !!mapId,
  });
  const englishQuery = useMapTranslations(mapId ?? '', 'en', {
    enabled: Boolean(mapId && isTranslationMode),
  });

  const [mapType, setMapType] = useState<MapType>('us');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [interactionMessage, setInteractionMessage] = useState(
    getBlockDefaultInteractionMessage('map') ?? ''
  );
  const [regions, setRegions] = useState<RegionEditorState[]>([]);
  const { mutateAsync: updateMap, isPending: isUpdating } = useUpdateMapVersion();

  const getContentSnapshot = useCallback(
    (): MapEditorSnapshot => ({ mapType, title, description, interactionMessage, regions }),
    [mapType, title, description, interactionMessage, regions]
  );

  const hydrateEditor = useCallback((snapshot: MapEditorSnapshot) => {
    setMapType(snapshot.mapType);
    setTitle(snapshot.title);
    setDescription(snapshot.description);
    setInteractionMessage(snapshot.interactionMessage);
    setRegions(snapshot.regions);
  }, []);

  const {
    saveStatus,
    saveErrors,
    replacementViews,
    persistChanges,
    promptForReplacement,
    showReplacementPrompt,
    applyReplacementDecision,
    beginSave,
    markSaved,
    markFailed,
    resetHydration,
    isSavingTranslation,
  } = useMapTranslationPersistence({
    mapId,
    selectedLocale,
    projection: translationQuery.data,
    getSnapshot: getContentSnapshot,
    hydrate: hydrateEditor,
    registerLocaleChangeGuard,
  });

  const handleMapTypeChange = useCallback(
    async (nextType: MapType) => {
      if (!mapId || isTranslationMode) return;
      const nextDescription = MAP_TYPE_DEFAULT_DESCRIPTIONS[nextType];
      const nextRegions = getRegionDefinitions(nextType).map((definition) => ({
        regionKey: definition.regionKey,
        name: definition.name,
        description: JSON.stringify(EMPTY_DOC),
      }));
      setMapType(nextType);
      setDescription(nextDescription);
      beginSave();
      try {
        await updateMap({
          id: mapId,
          input: { mapType: nextType, description: nextDescription, regions: nextRegions },
        });
        resetHydration();
        await queryClient.invalidateQueries({ queryKey: mapKeys.translation(mapId, 'en') });
        markSaved();
      } catch {
        markFailed();
      }
    },
    [
      beginSave,
      isTranslationMode,
      mapId,
      markFailed,
      markSaved,
      queryClient,
      resetHydration,
      updateMap,
    ]
  );

  const handleRegionDescriptionChange = useCallback(
    (regionKey: string, content: JSONContent) => {
      setRegions((prev) =>
        prev.map((r) => (r.regionKey === regionKey ? { ...r, description: content } : r))
      );
      const record = translationQuery.data?.regions.find((region) => region.regionKey === regionKey);
      if (record) promptForReplacement(record.regionId, record.summary.humanTranslationCount);
    },
    [promptForReplacement, translationQuery.data?.regions]
  );

  const handleRegionNameChange = useCallback((regionId: string, name: string) => {
    setRegions((current) =>
      current.map((region) => region.regionId === regionId ? { ...region, name } : region)
    );
    const record = translationQuery.data?.regions.find((region) => region.regionId === regionId);
    if (record) promptForReplacement(regionId, record.summary.humanTranslationCount);
  }, [promptForReplacement, translationQuery.data?.regions]);

  const hasRegionData = useMemo(
    () => regions.some((r) => !isRichTextContentEmpty(r.description)),
    [regions]
  );

  const [regionRevision, setRegionRevision] = useState(0);
  const handledRegionRevisionRef = useRef(0);
  const [showPreview, setShowPreview] = useState(false);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [csvImportWarning, setCsvImportWarning] = useState<{
    rows: Array<{ regionKey: string; name: string; description: string }>;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleDownloadCsvTemplate = useCallback(() => {
    const header = 'Region Code,Region Name,Description';
    const rows = regions.map((r) => {
      const html = richTextToHTML(r.description);
      return `${escapeCsvField(r.regionKey)},${escapeCsvField(r.name)},${escapeCsvField(html)}`;
    });
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || 'map'}-regions.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [regions, title]);

  const handleCsvFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (evt) => {
        const text = evt.target?.result;
        if (typeof text !== 'string') return;

        const parsed = parseCsvRows(text);
        const validKeys = new Set(regions.map((r) => r.regionKey));
        const invalidKeys = parsed
          .map((r) => r.regionKey)
          .filter((k) => !validKeys.has(k));

        if (invalidKeys.length > 0) {
          alert(
            `CSV import rejected. The following region codes do not match this map type:\n\n${invalidKeys.join(', ')}`
          );
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }

        if (parsed.length === 0) {
          alert('No valid rows found in the CSV file.');
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }

        setCsvImportWarning({ rows: parsed });
      };
      reader.readAsText(file);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [regions]
  );

  const handleConfirmCsvImport = useCallback(async () => {
    if (!csvImportWarning) return;
    const importMap = new Map(
      csvImportWarning.rows.map((r) => [r.regionKey, r.description])
    );
    setRegions((prev) =>
      prev.map((r) => {
        const imported = importMap.get(r.regionKey);
        if (imported !== undefined) {
          return {
            ...r,
            description: looksLikeHTML(imported) ? htmlToDoc(imported) : plainTextToDoc(imported),
          };
        }
        return r;
      })
    );
    setRegionRevision((v) => v + 1);
    setCsvImportWarning(null);
  }, [csvImportWarning]);

  useEffect(() => {
    if (
      regionRevision > 0
      && handledRegionRevisionRef.current !== regionRevision
    ) {
      handledRegionRevisionRef.current = regionRevision;
      void persistChanges();
    }
  }, [persistChanges, regionRevision]);

  const englishTopLevel = englishQuery.data?.topLevel.fields;
  const previewTitle = isTranslationMode ? title || englishTopLevel?.title || '' : title;
  const previewDescription =
    isTranslationMode ? description || englishTopLevel?.description || '' : description;
  const previewInteractionMessage =
    isTranslationMode
      ? interactionMessage || englishTopLevel?.interactionMessage || ''
      : interactionMessage;

  const previewBlock = useMemo((): MapBlock => {
    const now = new Date().toISOString();
    const englishRegions = englishQuery.data?.regions ?? [];
    const resolvedMapVersion: MapVersion = {
      id: mapId ?? 'preview',
      mapType,
      title: previewTitle,
      description: previewDescription,
      interactionMessage: previewInteractionMessage,
      regions: regions.map((r) => ({
        id: r.regionId,
        mapVersionId: mapId ?? 'preview',
        regionKey: r.regionKey,
        name: isTranslationMode
          ? r.name || englishRegions.find((candidate) => candidate.regionId === r.regionId)?.fields.name || ''
          : r.name,
        description: isTranslationMode && isRichTextContentEmpty(r.description)
          ? englishRegions.find((candidate) => candidate.regionId === r.regionId)?.fields.description ?? EMPTY_DOC
          : r.description,
        createdAt: now,
        updatedAt: now,
      })),
      createdAt: now,
      updatedAt: now,
    };
    return {
      id: 'preview-block',
      type: 'map',
      mapVersionId: mapId ?? '',
      title: previewTitle,
      description: previewDescription,
      resolvedMapVersion,
      createdAt: now,
      updatedAt: now,
    };
  }, [
    englishQuery.data?.regions,
    isTranslationMode,
    mapId,
    mapType,
    previewDescription,
    previewInteractionMessage,
    previewTitle,
    regions,
    translationQuery.data?.regions,
  ]);

  const { mutate: duplicateMap, isPending: isDuplicating } = useDuplicateMapVersion({
    onSuccess: (data) => {
      setShowDuplicateConfirm(false);
      navigate(`/maps/${data.id}`);
    },
  });

  const { mutate: deleteMap, isPending: isDeletingMap } = useDeleteMapVersion({
    onSuccess: () => {
      navigate(getRememberedListPath('/maps'), { replace: true });
    },
  });

  const handleDelete = useCallback(async () => {
    if (mapId && confirm('Are you sure you want to delete this map?') && await persistChanges()) {
      deleteMap(mapId);
    }
  }, [deleteMap, mapId, persistChanges]);

  const isLoading = translationQuery.isLoading || (isTranslationMode && englishQuery.isLoading);
  const isSaving = isSavingTranslation || isUpdating || saveStatus === 'saving';

  if (isLoading) {
    return <div style={styles.page}><p style={styles.loadingText}>Loading map...</p></div>;
  }
  if (translationQuery.isError || !translationQuery.data) {
    return <div style={styles.page}><p style={styles.loadingText}>Unable to load this map.</p></div>;
  }

  return (
    <div style={styles.page}>
      <header aria-label="Map editor header" style={styles.fixedTop}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <button
              type="button"
              onClick={() => void persistChanges().then((saved) => saved && navigate(getRememberedListPath('/maps')))}
              style={styles.backButton}
            >
              &larr; Back to Maps
            </button>
            <h2 style={styles.pageTitle}>Edit Map</h2>
          </div>
          <div style={styles.headerRight}>
            <button
              type="button"
              onClick={() => void persistChanges().then((saved) => saved && setShowPreview(true))}
              style={styles.secondaryButton}
            >
              Preview
            </button>
            <button
              type="button"
              onClick={() => void persistChanges().then((saved) => saved && setShowDuplicateConfirm(true))}
              style={styles.secondaryButton}
            >
              Duplicate
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeletingMap}
              style={styles.deleteButton}
            >
              {isDeletingMap ? 'Deleting...' : 'Delete'}
            </button>
            <span style={styles.saveStatus}>
              {isSaving ? 'Saving...'
                : saveStatus === 'saved' ? '✓ Saved'
                  : saveStatus === 'error' ? '⚠ Save failed'
                    : ''}
            </span>
          </div>
        </div>
      </header>

      {isTranslationMode && (
        <TranslationLanguageBanner language={selectedLanguageLabel} flush />
      )}

      {/* Scrollable form body */}
      <div style={styles.scrollArea}>
        <div style={styles.form}>
          <section aria-labelledby="map-details-heading" style={styles.metadataSection}>
            <h3 id="map-details-heading" style={styles.metadataSectionTitle}>
              Map Details
            </h3>
            <div role="group" aria-label="Map Details fields" style={styles.metadataCard}>
              {isTranslationMode && translationQuery.data.topLevel.status && (
                <div style={styles.metadataStatusRow}>
                  <TranslationStatusBadge status={translationQuery.data.topLevel.status} />
                </div>
              )}

              {!isTranslationMode && replacementViews['top-level'] && (
                <TranslationReplacementPrompt
                  humanTranslationCount={translationQuery.data.topLevel.summary.humanTranslationCount}
                  view={replacementViews['top-level']}
                  disabled={isSaving}
                  onQueue={() => void applyReplacementDecision('top-level', 'queue')}
                  onPreserve={() => void applyReplacementDecision('top-level', 'preserve')}
                  onChange={() => showReplacementPrompt('top-level')}
                />
              )}
              {isTranslationMode && translationQuery.data.topLevel.status && (
                <TranslationStatusNote status={translationQuery.data.topLevel.status} />
              )}
              {saveErrors['top-level'] && (
                <div role="alert" style={styles.saveError}>{saveErrors['top-level']}</div>
              )}

              <label style={styles.fieldLabel}>
                Title
                <input
                  type="text"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    promptForReplacement(
                      'top-level',
                      translationQuery.data.topLevel.summary.humanTranslationCount
                    );
                  }}
                  placeholder="e.g., US Harassment Laws"
                  style={styles.textInput}
                />
              </label>

              {!isTranslationMode && <label style={styles.fieldLabel}>
                Map Type
                <select
                  value={mapType}
                  onChange={(e) => void handleMapTypeChange(e.target.value as MapType)}
                  disabled={hasRegionData}
                  style={styles.select}
                >
                  {MAP_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {MAP_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
              </label>}

              <label style={styles.fieldLabel}>
                Description
                <textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    promptForReplacement(
                      'top-level',
                      translationQuery.data.topLevel.summary.humanTranslationCount
                    );
                  }}
                  placeholder="Optional description for this map..."
                  style={styles.textarea}
                  rows={3}
                />
              </label>

              <label style={styles.fieldLabel}>
                Default Interaction Message
                <input
                  type="text"
                  value={interactionMessage}
                  onChange={(e) => {
                    setInteractionMessage(e.target.value);
                    promptForReplacement(
                      'top-level',
                      translationQuery.data.topLevel.summary.humanTranslationCount
                    );
                  }}
                  style={styles.textInput}
                />
              </label>
            </div>
          </section>

          <section aria-labelledby="map-regions-heading" style={styles.regionsSection}>
            <div style={styles.regionsSectionHeader}>
              <h3 id="map-regions-heading" style={styles.regionsSectionTitle}>
                Regions ({regions.length})
              </h3>
              {!isTranslationMode && <div style={styles.csvButtonGroup}>
                <button
                  type="button"
                  onClick={handleDownloadCsvTemplate}
                  style={styles.csvButton}
                >
                  Download CSV
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={styles.csvButton}
                >
                  Import CSV
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleCsvFileSelect}
                  style={{ display: 'none' }}
                />
              </div>}
            </div>
            {regions.map((region) => {
              const translationRecord = translationQuery.data.regions.find(
                (candidate) => candidate.regionId === region.regionId
              );
              return <div key={`${region.regionId}-${regionRevision}`} style={styles.regionRow}>
                <div style={styles.regionHeader}>
                  <strong style={styles.regionCode}>{region.regionKey}</strong>
                  {isTranslationMode && translationRecord?.status && (
                    <TranslationStatusBadge status={translationRecord.status} />
                  )}
                </div>
                {!isTranslationMode && translationRecord && replacementViews[region.regionId] && (
                  <TranslationReplacementPrompt
                    humanTranslationCount={translationRecord.summary.humanTranslationCount}
                    view={replacementViews[region.regionId]!}
                    disabled={isSaving}
                    onQueue={() => void applyReplacementDecision(region.regionId, 'queue')}
                    onPreserve={() => void applyReplacementDecision(region.regionId, 'preserve')}
                    onChange={() => showReplacementPrompt(region.regionId)}
                  />
                )}
                {isTranslationMode && translationRecord?.status && (
                  <TranslationStatusNote status={translationRecord.status} />
                )}
                {saveErrors[region.regionId] && (
                  <div role="alert" style={styles.saveError}>{saveErrors[region.regionId]}</div>
                )}
                <label style={styles.fieldLabel}>
                  Title
                  <input
                    type="text"
                    value={region.name}
                    onChange={(event) => handleRegionNameChange(region.regionId, event.target.value)}
                    style={styles.textInput}
                  />
                </label>
                <label style={styles.fieldLabel}>Description</label>
                <RegionRichField
                  region={region}
                  onDescriptionChange={handleRegionDescriptionChange}
                  tokens={tokens}
                />
              </div>
            })}
          </section>
        </div>
      </div>

      {showPreview && (
        <div style={styles.previewOverlay}>
          <div style={styles.previewModal}>
            <div style={styles.previewHeader}>
              <h3 style={styles.previewTitle}>Map Preview</h3>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                style={styles.previewCloseButton}
              >
                Close
              </button>
            </div>
            <div style={styles.previewBody}>
              <ScopedI18nProvider locale={selectedLocale}>
                <MapRenderer block={previewBlock} />
              </ScopedI18nProvider>
            </div>
          </div>
        </div>
      )}

      {csvImportWarning && (
        <div style={styles.previewOverlay}>
          <div style={styles.csvImportModal}>
            <h3 style={styles.previewTitle}>Import CSV</h3>
            <p style={styles.csvImportText}>
              This will overwrite the descriptions for {csvImportWarning.rows.length} region(s).
              Any existing values for those regions will be replaced.
            </p>
            <p style={styles.csvImportText}>
              Do you want to continue?
            </p>
            <div style={styles.csvImportActions}>
              <button
                type="button"
                onClick={() => setCsvImportWarning(null)}
                style={styles.previewCloseButton}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmCsvImport}
                style={styles.saveButton}
              >
                Import
              </button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateConfirm && (
        <div style={styles.previewOverlay}>
          <div style={styles.csvImportModal}>
            <h3 style={styles.previewTitle}>Duplicate Map?</h3>
            <p style={styles.csvImportText}>
              Are you sure you want to duplicate &quot;{title || 'Untitled Map'}&quot;?
              A copy will be created with all regions and descriptions.
            </p>
            <div style={styles.csvImportActions}>
              <button
                type="button"
                onClick={() => { if (!isDuplicating) setShowDuplicateConfirm(false); }}
                style={styles.previewCloseButton}
                disabled={isDuplicating}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (mapId) duplicateMap(mapId);
                }}
                style={styles.saveButton}
                disabled={isDuplicating}
              >
                {isDuplicating ? 'Duplicating...' : 'Duplicate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function createStyles(tokens: ThemeTokens): Record<string, CSSProperties> {
  return {
    page: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    },
    fixedTop: {
      flexShrink: 0,
      backgroundColor: tokens.backgrounds.page,
      padding: `${tokens.spacing.lg} ${tokens.layout.sectionPaddingX.desktop}`,
      borderBottom: `1px solid ${tokens.borders.default}`,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
      zIndex: 10,
    },
    scrollArea: {
      flex: 1,
      overflowY: 'auto',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: tokens.spacing.lg,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.lg,
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.md,
    },
    backButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontSize: '14px',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.text.primary,
      cursor: 'pointer',
    },
    pageTitle: {
      margin: 0,
      fontSize: tokens.typography.body.size,
      fontWeight: 600,
      color: tokens.text.primary,
    },
    loadingText: {
      fontSize: '14px',
      color: tokens.text.muted,
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
      maxWidth: '800px',
      padding: `${tokens.spacing.lg} ${tokens.layout.sectionPaddingX.desktop} ${tokens.spacing.xl}`,
    },
    metadataSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
    },
    metadataSectionTitle: {
      margin: 0,
      fontSize: '15px',
      fontWeight: 600,
      color: tokens.text.primary,
    },
    metadataCard: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
      padding: tokens.spacing.lg,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      backgroundColor: tokens.backgrounds.surface,
    },
    metadataStatusRow: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    fieldLabel: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.text.primary,
    },
    textInput: {
      height: '38px',
      padding: `0 ${tokens.spacing.md}`,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '14px',
      fontWeight: 400,
      outline: 'none',
      boxSizing: 'border-box',
    },
    textarea: {
      padding: tokens.spacing.md,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '14px',
      fontWeight: 400,
      outline: 'none',
      resize: 'vertical',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
    },
    select: {
      height: '38px',
      padding: `0 ${tokens.spacing.md}`,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '14px',
      fontWeight: 400,
      outline: 'none',
    },
    regionsSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
    },
    regionsSectionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: tokens.spacing.md,
    },
    regionsSectionTitle: {
      margin: 0,
      fontSize: '15px',
      fontWeight: 600,
      color: tokens.text.primary,
    },
    csvButtonGroup: {
      display: 'flex',
      gap: tokens.spacing.sm,
    },
    csvButton: {
      padding: `${tokens.spacing.xs} ${tokens.spacing.md}`,
      fontSize: '13px',
      fontWeight: 500,
      color: tokens.text.primary,
      backgroundColor: 'transparent',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
    },
    regionRow: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
      padding: tokens.spacing.md,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.surface,
    },
    regionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: tokens.spacing.sm,
    },
    regionCode: {
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.text.primary,
    },
    saveError: {
      padding: tokens.spacing.sm,
      color: '#791f1f',
      backgroundColor: '#fcebeb',
      borderRadius: tokens.radius.sm,
      fontSize: '13px',
    },
    saveStatus: {
      fontSize: '13px',
      color: tokens.text.muted,
      minWidth: '80px',
      textAlign: 'right',
    },
    saveButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.xl}`,
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.text.onBrand,
      backgroundColor: tokens.brand.primary,
      border: 'none',
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
      boxShadow: tokens.shadow.sm,
    },
    secondaryButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.text.primary,
      backgroundColor: 'transparent',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
    },
    deleteButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.ui.errorText,
      backgroundColor: 'transparent',
      border: `1px solid ${tokens.ui.errorText}`,
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
    },
    previewOverlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    previewModal: {
      backgroundColor: tokens.backgrounds.page,
      borderRadius: tokens.radius.md,
      width: '90vw',
      maxWidth: '1000px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: tokens.shadow.lg,
    },
    previewHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: `${tokens.spacing.md} ${tokens.spacing.lg}`,
      borderBottom: `1px solid ${tokens.borders.default}`,
      flexShrink: 0,
    },
    previewTitle: {
      margin: 0,
      fontSize: '16px',
      fontWeight: 600,
      color: tokens.text.primary,
    },
    previewCloseButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.text.primary,
      backgroundColor: 'transparent',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
    },
    previewBody: {
      flex: 1,
      overflowY: 'auto',
      padding: tokens.spacing.lg,
    },
    csvImportModal: {
      backgroundColor: tokens.backgrounds.page,
      borderRadius: tokens.radius.md,
      padding: tokens.spacing.xl,
      width: '90vw',
      maxWidth: '480px',
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
      boxShadow: tokens.shadow.lg,
    },
    csvImportText: {
      margin: 0,
      fontSize: '14px',
      lineHeight: 1.5,
      color: tokens.text.primary,
    },
    csvImportActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: tokens.spacing.md,
      marginTop: tokens.spacing.sm,
    },
  };
}
